import { cn } from "@newxsystems/shared";
import type { CallToActionImageWidth } from "@newxsystems/types";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { ICallToActionSplitProps } from "./CallToActionSplit.props";

type SplitWidthClasses = {
  content: string;
  image: string;
  standalone: string;
};

const imageWidthClasses: Record<CallToActionImageWidth, SplitWidthClasses> = {
  small: {
    content: "lg:col-end-8",
    image: "w-1/2 lg:col-start-9 lg:w-full",
    standalone: "w-1/2",
  },
  medium: {
    content: "lg:col-end-6",
    image: "w-3/4 lg:col-start-7 lg:w-full",
    standalone: "w-3/4",
  },
  large: {
    content: "lg:col-end-5",
    image: "w-full lg:col-start-6",
    standalone: "w-full",
  },
};

const copyClasses = "min-w-0 lg:col-start-1 lg:self-start";

export const CallToActionSplit = ({
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  imageWidth = "medium",
  parentClass,
  spacing,
}: ICallToActionSplitProps) => {
  const resolvedImageWidth = imageWidth ?? "medium";
  const widthClasses = imageWidthClasses[resolvedImageWidth];

  if (!image) {
    const hasCopy = Boolean(eyebrow || heading || description);

    return (
      <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
        {hasCopy && buttons?.length ? (
          <div className="grid min-w-0 items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <BlockCommonContent
              alignment="start"
              description={description}
              eyebrow={eyebrow}
              heading={heading}
              headingLevel="h2"
            />
            <BlockActions actions={buttons} alignment="start" />
          </div>
        ) : hasCopy ? (
          <BlockCommonContent
            alignment="start"
            description={description}
            eyebrow={eyebrow}
            heading={heading}
            headingLevel="h2"
          />
        ) : buttons?.length ? (
          <BlockActions actions={buttons} alignment="start" />
        ) : null}
      </BlockContainer>
    );
  }

  const hasLeftContent = Boolean(eyebrow || heading || description || buttons?.length);

  if (!hasLeftContent) {
    return (
      <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
        <div className={widthClasses.standalone}>
          <BlockImage
            aspectRatioClassName="overflow-hidden rounded-[14px] border border-foreground"
            effectiveAspectRatio={image.aspectRatio ?? "16:9"}
            image={image}
            imageClassName="object-cover"
          />
        </div>
      </BlockContainer>
    );
  }

  const actionClasses = cn(copyClasses, widthClasses.content, "lg:mt-2");

  return (
    <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
      <div className="grid min-w-0 gap-6 lg:grid-cols-12 lg:gap-x-6 lg:gap-y-6">
        {eyebrow ? (
          <p className={cn(copyClasses, widthClasses.content, "font-mono text-[12px]/[1.45]")}>
            {eyebrow}
          </p>
        ) : null}
        <div
          className={cn(
            "min-w-0 justify-self-start lg:col-end-13 lg:row-start-1 lg:row-end-[span_4] lg:self-center",
            widthClasses.image,
          )}
        >
          <BlockImage
            aspectRatioClassName="overflow-hidden rounded-[14px] border border-foreground"
            effectiveAspectRatio={image.aspectRatio ?? "16:9"}
            image={image}
            imageClassName="object-cover"
          />
        </div>
        {heading ? (
          <h2
            className={cn(
              copyClasses,
              widthClasses.content,
              "max-w-[18ch] text-balance text-[34px]/[1] font-semibold tracking-[-0.025em] [overflow-wrap:anywhere] md:text-[44px]",
            )}
          >
            {heading}
          </h2>
        ) : null}
        {description ? (
          <BlockRichText
            className={cn(
              copyClasses,
              widthClasses.content,
              "max-w-[62ch] text-base/[1.55] text-pretty text-onSurface-500 md:text-[17px] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc",
            )}
            content={description}
          />
        ) : null}
        {buttons?.length ? (
          <BlockActions actions={buttons} alignment="start" className={actionClasses} />
        ) : null}
      </div>
    </BlockContainer>
  );
};

CallToActionSplit.displayName = "CallToAction::CallToActionSplit";
